import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FiBriefcase,
  FiCalendar,
  FiClock,
  FiCreditCard,
  FiHome,
  FiLayers,
  FiLock,
  FiMapPin,
  FiPhone,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import "./LoanForm1.css";
import SelfieCapture from "./SelfieCapture";
import STATE_CITY from "../../utils/stateCityMapping";
import { checkCustomerByPan } from "../../utils/panCustomerCheck";
import { useNavigate } from "react-router-dom";

const getApiMessage = (data) =>
  data?.message ?? data?.Message ?? data?.error ?? "";

const IconField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
  </div>
);

const isApiSuccess = (data) => {
  if (!data || typeof data !== "object") return false;

  const success = data.success ?? data.Success;
  const status = data.status ?? data.Status;
  const message = getApiMessage(data).toLowerCase();

  if (
    success === false ||
    success === 0 ||
    success === "0" ||
    success === "false"
  ) {
    return false;
  }

  if (
    status === false ||
    status === 0 ||
    status === "0"
  ) {
    return false;
  }

  if (
    message.includes("already exists") ||
    message.includes("cannot apply") ||
    message.includes("failed")
  ) {
    return false;
  }

  if (
    success === true ||
    success === 1 ||
    success === "1" ||
    success === "true"
  ) {
    return true;
  }

  if (status == 1) return true;

  if (data.savedBankPath) return true;

  return false;
};

const PAN_STATUS_VERIFIED = "PAN Verification Complete!";
const PAN_STATUS_CONTINUE =
  "Online PAN verification unavailable. You can continue and submit your application.";

const isPanNotFoundOnline = (responseCode, responseMessage) =>
  responseCode === "ENI004" ||
  String(responseMessage || "").toLowerCase().includes("no information found");

const isPanAcceptedForSubmit = (status) => {
  const text = String(status || "").toLowerCase();
  return (
    text.includes("complete") ||
    text.includes("continue") ||
    text.includes("re-apply")
  );
};

const DOCUMENT_FILE_RULES = {
  aadhaarFront: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    label: "PDF, JPG, PNG",
  },
  aadhaarBack: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    label: "PDF, JPG, PNG",
  },
  panCard: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    label: "PDF, JPG, PNG",
  },
  bankStatement: {
    accept: ".pdf,application/pdf",
    extensions: ["pdf"],
    label: "PDF only",
  },
  salarySlip: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    label: "PDF, JPG, PNG",
  },
  utilityBill: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    label: "PDF, JPG, PNG",
  },
};

const isAllowedDocumentFile = (file, rules) => {
  if (!file) return false;

  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return rules.extensions.includes(extension);
};

const DocumentAcceptHint = ({ rules }) => (
  <span className="loan-field-hint">{rules.label}</span>
);

const Req = () => <span className="req">*</span>;

const SectionHeader = ({ title, description, badge }) => (
  <div className="apply-now1-section-head">
    <div className="apply-now1-section-head__copy">
      <p className="apply-form-block__title">{title}</p>
      {description ? <p>{description}</p> : null}
    </div>
    {badge ? <span className="apply-now1-section-badge">{badge}</span> : null}
  </div>
);

const SelectedFileText = ({ file }) =>
  file ? <div className="apply-now1-doc-file">Selected: {file.name}</div> : null;

const LoanForm1 = ({ onBack, onNext }) => {
  const navigate = useNavigate();
  const [pan, setPan] = useState("");
  const [panStatus, setPanStatus] = useState("");
  const [panVerifying, setPanVerifying] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [earlyLeadId, setEarlyLeadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const panVerifyLockRef = useRef(false);
  const [autofillData, setAutofillData] = useState(null);
  const [panDetails, setPanDetails] = useState({});

  // Form states
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [obligation, setObligation] = useState("");


const [incomeError, setIncomeError] = useState("");
const [loanError, setLoanError] = useState("");
const [obligationError, setObligationError] = useState("");
const [panError, setPanError] =
  useState("");

const [salarySlipError, setSalarySlipError] =
  useState("");

  const [bankStatementError, setBankStatementError] =
  useState("");

const [utilityBillError, setUtilityBillError] =
  useState("");

const [aadhaarFrontError, setAadhaarFrontError] =
  useState("");

const [aadhaarBackError, setAadhaarBackError] =
  useState("");

const [panCardError, setPanCardError] =
  useState("");

const [selfieError, setSelfieError] =
  useState("");
  // Document Uploads
  const [bankStatement, setBankStatement] = useState(null);
  const [salarySlip, setSalarySlip] = useState(null);
  const [bankStatementPassword, setBankStatementPassword] = useState("");
  const [salarySlipPassword, setSalarySlipPassword] = useState("");
  const [utilityBill, setUtilityBill] = useState(null);
const [aadhaarFront, setAadhaarFront] =
  useState(null);

const [aadhaarBack, setAadhaarBack] =
  useState(null);

const [panCardFile, setPanCardFile] =
  useState(null);

  // Selfie
  const [selfie, setSelfie] = useState("");
  const [showCameraModal, setShowCameraModal] = useState(false);

  useEffect(() => {
    const storedData = localStorage.getItem("loanFormData");
    if (storedData) {
      setAutofillData(JSON.parse(storedData));
    }
  }, []);

  // --- YE PURA BLOCK YAHAN PASTE KAREIN ---
  useEffect(() => {
  const incomeNum =
    Number(monthlyIncome);

  const loanNum =
    Number(loanAmount);

  const obligationNum =
    Number(obligation);

  // RESET
  setIncomeError("");
  setLoanError("");
  setObligationError("");

  // ======================
  // MONTHLY INCOME
  // ======================

 if (
  monthlyIncome !== ""
) {

  // ALL ZERO
  if (
    /^0+$/.test(
      monthlyIncome
    )
  ) {
    setIncomeError(
      "Monthly income cannot be zero"
    );
  }

  // FAKE / REPEATED
  else if (
    isFakeRepeatedNumber(
      monthlyIncome
    )
  ) {
    setIncomeError(
      "Please enter your actual monthly salary"
    );
  }

  // MINIMUM ELIGIBILITY
  else if (
    incomeNum <= 50000
  ) {
    setIncomeError(
      "Minimum monthly income should be more than ₹50,000"
    );
  }
}

  // ======================
  // LOAN AMOUNT
  // ======================

  if (
    loanAmount !== ""
  ) {
    if (
      /^0+$/.test(loanAmount)
    ) {
      setLoanError(
        "Loan amount cannot be zero"
      );
    } else if (
      loanNum >= incomeNum
    ) {
      setLoanError(
        "Loan amount must be less than monthly income"
      );
    }
  }

  // ======================
  // EMI OBLIGATION
  // ======================

  if (
    obligation !== ""
  ) {
    if (
      /^0+$/.test(obligation)
    ) {
      setObligationError(
        "Invalid EMI obligation"
      );
    } else if (
      isFakeRepeatedNumber(
        obligation
      )
    ) {
      setObligationError(
        "Enter valid EMI obligation"
      );
    } else if (
      obligationNum >= incomeNum
    ) {
      setObligationError(
        "EMI obligation must be less than monthly income"
      );
    }
  }
}, [
  monthlyIncome,
  loanAmount,
  obligation,
]);

  // Get Aadhaar data from localStorage
  const getAadhaarData = () => {
    const aadhaarData = localStorage.getItem("aadhaarData");
    return aadhaarData ? JSON.parse(aadhaarData) : null;
  };

  const handleCapture = (img) => setSelfie(img);
// REPEATED / FAKE NUMBER CHECK
const isFakeRepeatedNumber = (value) => {
  if (!value) return false;

  // All same digits: 11111, 22222, 00000
  if (/^(\d)\1+$/.test(value)) {
    return true;
  }

  // Sequential ascending: 1234, 12345, 123456
  if (value.length >= 4 && "0123456789".includes(value)) {
    return true;
  }

  // Sequential descending: 9876, 87654, 765432
  if (value.length >= 4 && "9876543210".includes(value)) {
    return true;
  }

  // Repeating chunks: 121212, 123123, 343434, 12341234
  if (/^(\d{2,4})\1+$/.test(value)) {
    return true;
  }

  // First half same as second half: 100100, 50005000
  if (value.length >= 6 && value.length % 2 === 0) {
    const half = value.length / 2;
    if (value.slice(0, half) === value.slice(half)) {
      return true;
    }
  }

  return false;
};

  const handlePanVerify = async (val) => {
    if (!val || val.length !== 10) return;
    if (panVerifyLockRef.current) return;

    panVerifyLockRef.current = true;
    setPanVerifying(true);
    setPanError("");
    setPanStatus("");

    const verifyPanUrl =
      "https://fundswalla.in/loan_backend/verifyPan.php";

    try {
      const customerCheck = await checkCustomerByPan(val);

      if (customerCheck.blocked) {
        setPanDetails({});
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanStatus("");
        setPanError(
          customerCheck.message ||
            "This PAN is already registered. Cannot re-apply."
        );
        return;
      }

      const statusMessage =
        customerCheck.isFresh || !customerCheck.message
          ? PAN_STATUS_VERIFIED
          : customerCheck.message;

      // verifyPan.php reads $_GET['pan']; a POST body is ignored and returns "PAN number required".
      const verifyRes = await axios.get(verifyPanUrl, {
        params: { pan: val },
        timeout: 30000,
      });
      const verifyData = verifyRes.data?.data || {};

      console.log("PAN RESPONSE =", verifyRes.data);
      console.log("PAN STATUS CHECK =", customerCheck);

      const panInfo = verifyData?.data || {};
      const responseCode = verifyData.responseCode || "";
      const responseMessage = verifyData.responseMessage || "";
      const aadhaarLinked =
        panInfo.aadhaarLinked === true ||
        panInfo.aadhaarLinked === "true" ||
        panInfo.aadhaarLinked === "Y"
          ? "true"
          : panInfo.aadhaarLinked === false ||
            panInfo.aadhaarLinked === "false" ||
            panInfo.aadhaarLinked === "N"
            ? "false"
            : "";

      const nextPanDetails = {
        pan_responseCode: responseCode,
        pan_responseMessage: responseMessage,
        pan_referenceId: verifyData.referenceId || "",
        pan_firstName: panInfo.firstName || "",
        pan_lastName: panInfo.lastName || "",
        pan_address: panInfo.address || "",
        pan_gender: panInfo.gender || "",
        pan_aadhaarLinked: aadhaarLinked,
        pan_dob: panInfo.dob || "",
        pan_fullName: panInfo.fullName || "",
        pan_category: panInfo.category || "",
        pan_maskedAadhaarNumber: panInfo.maskedAadhaarNumber || "",
        pan_verifiedPan: panInfo.pan || val,
      };

      if (responseCode === "SRC001") {
        setPanDetails(nextPanDetails);
        setPanStatus(statusMessage);
        setPanVerified(true);
        localStorage.setItem("pan_verified", "true");
        return;
      }

      if (isPanNotFoundOnline(responseCode, responseMessage)) {
        setPanDetails({
          ...nextPanDetails,
          pan_firstName: "",
          pan_lastName: "",
          pan_address: "",
          pan_gender: "",
          pan_aadhaarLinked: "",
          pan_dob: "",
          pan_fullName: "",
          pan_category: "",
          pan_maskedAadhaarNumber: "",
          pan_verifiedPan: val,
        });
        // Bucket already allowed (fresh/reject/close) — let user continue even if online PAN lookup missed.
        setPanStatus(statusMessage || PAN_STATUS_CONTINUE);
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        return;
      }

      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("Please enter a valid PAN number.");
    } catch (err) {
      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("PAN verification failed. Please try again.");
      setPanStatus("");
    } finally {
      panVerifyLockRef.current = false;
      setPanVerifying(false);
    }
  };

  const handleIncomeChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 8) {
        setMonthlyIncome(value);
      }
    }
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 10) {
        setLoanAmount(value);
      }
    }
  };

  const handleObligationChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 7) {
        setObligation(value);
      }
    }
  };

  const handleDocumentChange = (e, rules, setFile, setError) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFile(null);
      setError("");
      return;
    }

    if (!isAllowedDocumentFile(file, rules)) {
      setFile(null);
      setError(`Only ${rules.label} files are allowed`);
      e.target.value = "";
      return;
    }

    setError("");
    setFile(file);
  };

  // FINAL SUBMIT - Original working code
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Pehle check karein ki eligibility error toh nahi hai
    if (incomeError || loanError) {
      setSubmitting(false);
      return; 
    }

    if (submitting || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
  

    // VALIDATION
    // RESET ERRORS
setIncomeError("");
setLoanError("");
setObligationError("");
setSalarySlipError("");
setBankStatementError("");
setUtilityBillError("");
setAadhaarFrontError("");
setAadhaarBackError("");
setPanCardError("");
setSelfieError("");

let hasError = false;

// PAN
if (panError) {
  hasError = true;
} else if (
  !pan ||
  pan.length !== 10 ||
  panVerifying ||
  !isPanAcceptedForSubmit(panStatus)
) {
  setPanError(
    panVerifying
      ? "PAN verification in progress"
      : "Please enter your PAN and wait for verification"
  );

  hasError = true;
}

// INCOME
if (!monthlyIncome) {
  setIncomeError(
    "Monthly income is required"
  );

  hasError = true;
}

// LOAN
if (!loanAmount) {
  setLoanError(
    "Loan amount is required"
  );

  hasError = true;
}

// EMI
if (obligation === "") {
  setObligationError(
    "EMI obligation is required"
  );

  hasError = true;
}

// DOCUMENTS
if (!aadhaarFront) {
  setAadhaarFrontError(
    "Aadhaar front is required"
  );

  hasError = true;
}

if (!aadhaarBack) {
  setAadhaarBackError(
    "Aadhaar back is required"
  );

  hasError = true;
}

if (!panCardFile) {
  setPanCardError(
    "PAN card is required"
  );

  hasError = true;
}

if (!bankStatement) {
  setBankStatementError(
    "Bank statement is required"
  );

  hasError = true;
}

if (!salarySlip) {
  setSalarySlipError(
    "Salary slip is required"
  );

  hasError = true;
}

if (!utilityBill) {
  setUtilityBillError(
    "Utility bill is required"
  );

  hasError = true;
}

// SELFIE
if (!selfie) {
  setSelfieError(
    "Selfie is required"
  );

  hasError = true;
}

if (hasError) {
  submitLockRef.current = false;
  setSubmitting(false);
  setTimeout(() => {
    const firstError =
      document.querySelector(".apply-now1-page .loan-field.has-error") ||
      document.querySelector(".apply-now1-page .loan-field-error");
    if (firstError) {
      firstError.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 0);
  return;
}

    if (!autofillData) {
      alert("Autofill data not found. Please restart the process.");
      submitLockRef.current = false;
      setSubmitting(false);
      return;
    }

    // Get Aadhaar data for submission
    const aadhaarData = getAadhaarData();
    
    // Get Aadhaar number directly from localStorage (stored by AadhaarInput.js)
    const aadhaarNumber = String(
      localStorage.getItem("aadhaarNumber") ||
        aadhaarData?.aadhaarNumber ||
        aadhaarData?.aadhaar_number ||
        ""
    ).replace(/\D/g, "");
    const aadharNoLast4 =
      aadhaarNumber.length >= 4 ? aadhaarNumber.slice(-4) : "";
    const mobileOtpValue = String(
      localStorage.getItem("mobile_otp") || autofillData?.otp || ""
    ).trim();
    const aadhaarMobile = String(
      localStorage.getItem("aadhaar_mobile") ||
        aadhaarData?.mobile ||
        aadhaarData?.mobileNumber ||
        aadhaarData?.phone ||
        aadhaarData?.registeredMobile ||
        ""
    )
      .replace(/\D/g, "")
      .slice(-10);
    
    // Debug: Log Aadhaar data structure
    console.log("Aadhaar Data from localStorage:", aadhaarData);
    console.log("Aadhaar Number from localStorage:", aadhaarNumber);
    console.log("Available fields in Aadhaar data:", aadhaarData ? Object.keys(aadhaarData) : "No data");

    const isPanStatusTrue =
      panVerified === true ||
      localStorage.getItem("pan_verified") === "true" ||
      panStatus === PAN_STATUS_VERIFIED;

    const panPayloadFields = {
      pan_status: isPanStatusTrue,
      pan_responseCode: panDetails.pan_responseCode || "",
      pan_responseMessage: panDetails.pan_responseMessage || "",
      pan_referenceId: panDetails.pan_referenceId || "",
      pan_firstName: panDetails.pan_firstName || "",
      pan_lastName: panDetails.pan_lastName || "",
      pan_address: panDetails.pan_address || "",
      pan_gender: panDetails.pan_gender || "",
      pan_aadhaarLinked: panDetails.pan_aadhaarLinked || "",
      pan_dob: panDetails.pan_dob || "",
      pan_fullName: panDetails.pan_fullName || "",
      pan_maskedAadhaarNumber: panDetails.pan_maskedAadhaarNumber || "",
      pan_category: panDetails.pan_category || "",
      pan_verifiedPan: panDetails.pan_verifiedPan || pan || "",
    };

    const payload = {
      customer_id: "",
      loan_amount: loanAmount,
      monthly_income: monthlyIncome,
      obligations: obligation,
      state_id: autofillData.state_id,
      city: autofillData.city_id,
      pincode: autofillData.pincode,
      first_name: autofillData.firstName,
      middle_name: autofillData.lastName,
      gender: autofillData.gender === "MALE" ? "M" : autofillData.gender === "FEMALE" ? "F" : "O",
      dob: autofillData.dob,
      pan: pan,
      pan_verified: isPanStatusTrue ? "true" : "false",
      mobile: autofillData.mobile,
      email_personal: autofillData.email,
      source: autofillData.pincode,
      mobile_verified:
  localStorage.getItem("mobile_verified") || "false",
      otp: mobileOtpValue,
      aadhar_no: aadharNoLast4,
      aadhaar_mobile: aadhaarMobile,
      // Add Aadhaar data to payload with better field mapping
      aadhaar_number: aadhaarNumber || aadhaarData?.referenceId || "",
      aadhaar_name: aadhaarData?.name || "",
      aadhaar_dob: aadhaarData?.dateOfBirth || aadhaarData?.dob || "",
      aadhaar_gender: aadhaarData?.gender || "",
      // PAN Number details
      ...panPayloadFields,
      aadhaar_address: autofillData?.address || (() => {
        if (aadhaarData?.address) return aadhaarData.address;
        if (aadhaarData?.house) {
          const addressParts = [
            aadhaarData.house,
            aadhaarData?.street || "",
            aadhaarData?.landmark || "",
            aadhaarData?.locality || "",
            aadhaarData?.vtcName || "",
            aadhaarData?.district || "",
            aadhaarData?.state || "",
            aadhaarData?.pincode || ""
          ];
          return addressParts.filter(part => part.trim() !== "").join(", ");
        }
        return "";
      })(),
      aadhaar_pincode: aadhaarData?.pincode || "",
      aadhaar_state: aadhaarData?.state || "",
      aadhaar_district: aadhaarData?.district || aadhaarData?.vtcName || "",
    };

    const base64ToFile = (base64, filename = null) => {
      if (!base64) return null;

      const arr = base64.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      const finalName =
        filename || `document_${Date.now()}.${mime.split("/")[1]}`;

      return new File([u8arr], finalName, { type: mime });
    };

    console.log("FINAL PAYLOAD:", payload);

    try {
      let lead_id = earlyLeadId;

      if (!lead_id) {
        const res = await axios.post("https://fundswalla.in/loan_backend/saveLendingPage.php", payload);

        console.log("SAVE RESPONSE:", res.data);
        
        // Check if response contains HTML error (PHP fatal error)
        if (typeof res.data === 'string' && res.data.includes('<b>Fatal error</b>')) {
          alert("Server error occurred. Please try again later.");
          return;
        }
        
        // Check if response is valid JSON/object
        if (!res.data || typeof res.data !== 'object') {
          alert("Server response error. Please try again later.");
          return;
        }
        
        lead_id = res.data.inserted_id;
        const message = getApiMessage(res.data);

        if (!isApiSuccess(res.data)) {
          alert(message || "Application could not be submitted. Please try again.");
          return;
        }

        if (!lead_id) {
          alert(message || "Application could not be submitted. Please try again.");
          return;
        }
      }

      const selfieFile = selfie ? base64ToFile(selfie, "selfie.jpg") : null;

      const formData = new FormData();
      formData.append("lead_id", lead_id);
      formData.append("pan", pan);
      formData.append("pan_verified", isPanStatusTrue ? "true" : "false");
      formData.append("pan_status", isPanStatusTrue ? "true" : "false");
      formData.append("pan_responseCode", panPayloadFields.pan_responseCode);
      formData.append("pan_responseMessage", panPayloadFields.pan_responseMessage);
      formData.append("pan_referenceId", panPayloadFields.pan_referenceId);
      formData.append("pan_firstName", panPayloadFields.pan_firstName);
      formData.append("pan_lastName", panPayloadFields.pan_lastName);
      formData.append("pan_address", panPayloadFields.pan_address);
      formData.append("pan_gender", panPayloadFields.pan_gender);
      formData.append("pan_aadhaarLinked", panPayloadFields.pan_aadhaarLinked);
      formData.append("pan_dob", panPayloadFields.pan_dob);
      formData.append("pan_fullName", panPayloadFields.pan_fullName);
      formData.append("pan_maskedAadhaarNumber", panPayloadFields.pan_maskedAadhaarNumber);
      formData.append("pan_category", panPayloadFields.pan_category);
      formData.append("pan_verifiedPan", panPayloadFields.pan_verifiedPan);
      formData.append("mobile", autofillData.mobile);
      formData.append(
        "mobile_verified",
        localStorage.getItem("mobile_verified") || "false"
      );
      formData.append("otp", mobileOtpValue);
      formData.append("aadhar_no", aadharNoLast4);
      formData.append("aadhaar_mobile", aadhaarMobile);
      formData.append("firstName", autofillData.firstName);
      formData.append("lastName", autofillData.lastName);
      formData.append("dob", autofillData.dob);
      formData.append("address", autofillData.address);
      formData.append("stateId", autofillData.state_id);
      formData.append("cityId", autofillData.city_id);
      formData.append("pincode", autofillData.pincode);
      formData.append("gender", autofillData.gender);
      formData.append("monthlyIncome", monthlyIncome);
      formData.append("monthly_income", monthlyIncome);
      formData.append("loanAmount", loanAmount);
      formData.append("obligation", obligation);
      formData.append("email", autofillData.email);
      formData.append("state", autofillData.state);
      formData.append("city", autofillData.district);
      
      // Add Aadhaar data to FormData with better field mapping
      if (aadhaarData) {
        const getAadhaarAddress = () => {
          // First try autofillData address, then Aadhaar data
          if (autofillData?.address) return autofillData.address;
          if (aadhaarData?.address) return aadhaarData.address;
          if (aadhaarData?.house) {
            const addressParts = [
              aadhaarData.house,
              aadhaarData?.street || "",
              aadhaarData?.landmark || "",
              aadhaarData?.locality || "",
              aadhaarData?.vtcName || "",
              aadhaarData?.district || "",
              aadhaarData?.state || "",
              aadhaarData?.pincode || ""
            ];
            return addressParts.filter(part => part.trim() !== "").join(", ");
          }
          return "";
        };

        formData.append("aadhaar_number", aadhaarNumber || aadhaarData?.aadhaarNumber || aadhaarData?.aadhaar_number || aadhaarData?.referenceId || "");
        formData.append("aadhaar_name", aadhaarData?.name || "");
        formData.append("aadhaar_dob", aadhaarData?.dateOfBirth || aadhaarData?.dob || "");
        formData.append("aadhaar_gender", aadhaarData?.gender || "");
        formData.append("aadhaar_address", getAadhaarAddress());
        formData.append("aadhaar_pincode", aadhaarData?.pincode || "");
        formData.append("aadhaar_state", aadhaarData?.state || "");
        formData.append("aadhaar_district", aadhaarData?.district || aadhaarData?.vtcName || "");
      }

      if (selfieFile) formData.append("selfie", selfieFile);
      if (bankStatement) formData.append("bank_statement", bankStatement);
      if (salarySlip) formData.append("salary_slip", salarySlip);
      if (bankStatementPassword.trim()) {
        formData.append("password", bankStatementPassword.trim());
      }
      if (salarySlipPassword.trim()) {
        formData.append("pwd", salarySlipPassword.trim());
      }
      if (utilityBill) formData.append("utility_bill", utilityBill);

      if (aadhaarFront)
  formData.append(
    "aadharfront",
    aadhaarFront
  );

if (aadhaarBack)
  formData.append(
    "aadhar",
    aadhaarBack
  );

if (panCardFile)
  formData.append(
    "pan",
    panCardFile
  );

      try {
          const saveRes = await axios.post("https://fundswalla.in/loan_backend/submit.php",formData,{
            headers: { "Content-Type": "multipart/form-data" },
          });

          console.log("SAVE RESPONSE:", saveRes.data);
          
          // Check if response contains HTML error (PHP fatal error)
          if (typeof saveRes.data === 'string' && saveRes.data.includes('<b>Fatal error</b>')) {
            alert("Server error occurred. Please try again later.");
            return;
          }
          
          // Check if response is valid JSON/object
          if (!saveRes.data || typeof saveRes.data !== 'object') {
            alert("Server response error. Please try again later.");
            return;
          }
          
          const savedBankPath = saveRes.data.savedBankPath;
          const submitMessage = getApiMessage(saveRes.data);

          if (!isApiSuccess(saveRes.data)) {
            alert(submitMessage || "Application could not be submitted. Please try again.");
            return;
          }

        let referenceId = null;

        if (savedBankPath) {
          try {
            const uploadRes = await axios.post(
              "https://fundswalla.in/loan_backend/uploadBankStatement.php",
              {
                filePath: savedBankPath,
                password: bankStatementPassword.trim() || "",
              }
            );

            // Check if response contains HTML error (PHP fatal error)
            if (typeof uploadRes.data === 'string' && uploadRes.data.includes('<b>Fatal error</b>')) {
              alert("Server error occurred. Please try again later.");
              return;
            }
            
            referenceId =
              uploadRes.data?.data?.referenceId ||
              uploadRes.data?.referenceId ||
              null;
          } catch (uploadErr) {
            console.error("Upload Error:", uploadErr);
            alert("Error uploading documents. Please try again.");
            return;
          }
        }

        // Store lead ID for LMS tracking
        localStorage.setItem("currentLeadId", lead_id);

        // Prefer in-flow thank-you (ApplyNow1 step 4); fallback to /thank-you route
        if (typeof onNext === "function") {
          onNext();
        } else {
          navigate("/thank-you");
        }
        } catch (submitErr) {
          console.error("Submit Error:", submitErr);
          const submitErrorMessage =
            getApiMessage(submitErr?.response?.data) ||
            "Something went wrong. Please try again later.";

          if (submitErr.code === 'ERR_NETWORK' || submitErr.code === 'ECONNREFUSED') {
            alert("Network error. Please check your internet connection and try again.");
          } else if (submitErr.response?.status >= 500) {
            alert("Server error occurred. Please try again later.");
          } else if (submitErr.response?.status >= 400) {
            alert(submitErrorMessage);
          } else {
            alert(submitErrorMessage);
          }
          return;
        }

    } catch (err) {
      console.error("API Error:", err);
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') {
        alert("Network error. Please check your internet connection and try again.");
      } else if (err.response?.status >= 500) {
        alert("Server error occurred. Please try again later.");
      } else if (err.response?.status >= 400) {
        alert("Request error. Please try again.");
      } else {
        alert("Something went wrong. Please try again later.");
      }
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (!autofillData) {
    return (
      <div className="apply-form-head">
        <h2>Loading...</h2>
        <p>Preparing your application</p>
      </div>
    );
  }

  return (
    <div className="loanform-wrapper">
      <div className="loanform-card">
        <form onSubmit={handleSubmit}>
          <div className="apply-form-head">
            <div className="apply-now1-hero">
              <span className="apply-now1-hero__eyebrow">Final Step</span>
              <h2>Complete Application</h2>
              <p>PAN, loan details, documents and selfie.</p>
            </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title="1. Loan Details"
              description=""
              badge=""
            />

          <div className="loan-form-grid loan-form-grid--4">
            <div className={`loan-field ${panError ? "has-error" : ""}`}>
              <label>PAN Number <Req /></label>
              <IconField icon={FiCreditCard}>
                <input
                  type="text"
                  maxLength="10"
                  placeholder="Enter PAN"
                  value={pan}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setPan(v);
                    setPanError("");
                    setPanStatus("");
                    setEarlyLeadId(null);
                    if (v.length === 10) handlePanVerify(v);
                  }}
                  disabled={panVerifying}
                />
              </IconField>
              {panVerifying && (
                <p style={{ color: "#1a3648", margin: "4px 0", fontSize: "0.75rem" }}>
                  Verifying PAN...
                </p>
              )}
              {panStatus && !panError && (
                <p
                  style={{
                    color: panStatus.includes("Complete")
                      ? "#67272d"
                      : panStatus.toLowerCase().includes("re-apply")
                        ? "#67272d"
                        : panStatus.toLowerCase().includes("continue")
                          ? "#67272d"
                          : "#64748b",
                    margin: "4px 0",
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                  }}
                >
                  {panStatus}
                </p>
              )}
              {panError && <p className="loan-field-error">{panError}</p>}
            </div>

            <div className={`loan-field ${incomeError ? "has-error" : ""}`}>
              <label>Monthly Income <Req /></label>
              <IconField icon={FaIndianRupeeSign}>
                <input type="text" value={monthlyIncome} onChange={handleIncomeChange} placeholder="Monthly Income" />
              </IconField>
              {incomeError && <p className="loan-field-error">{incomeError}</p>}
            </div>

            <div className={`loan-field ${loanError ? "has-error" : ""}`}>
              <label>Loan Amount <Req /></label>
              <IconField icon={FiBriefcase}>
                <input type="text" value={loanAmount} onChange={handleLoanAmountChange} placeholder="Required Amount" />
              </IconField>
              {loanError && <p className="loan-field-error">{loanError}</p>}
            </div>

            <div className={`loan-field ${obligationError ? "has-error" : ""}`}>
              <label>EMI Obligations <Req /></label>
              <IconField icon={FiClock}>
                <input
                  type="text"
                  value={obligation}
                  onChange={handleObligationChange}
                  placeholder="Existing EMI"
                />
              </IconField>
              {obligationError && <p className="loan-field-error">{obligationError}</p>}
            </div>
          </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title={<>2. Your Profile <span className="loan-readonly-tag">Read-only</span></>}
              description=""
              badge=""
            />

          <div className="loan-form-grid loan-form-grid--4">
            <div className="loan-field">
              <label>First Name</label>
              <IconField icon={FiUser}>
                <input value={autofillData.firstName} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>Last Name</label>
              <IconField icon={FiUser}>
                <input value={autofillData.lastName} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>Mobile Number</label>
              <IconField icon={FiPhone}>
                <input value={autofillData.mobile} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>Date of Birth</label>
              <IconField icon={FiCalendar}>
                <input value={autofillData.dob} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>Gender</label>
              <IconField icon={FiUsers}>
                <input value={autofillData.gender} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>Pincode</label>
              <IconField icon={FiMapPin}>
                <input value={autofillData.pincode} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>State</label>
              <IconField icon={FiLayers}>
                <input value={autofillData.state} disabled />
              </IconField>
            </div>

            <div className="loan-field">
              <label>City / District</label>
              <IconField icon={FiHome}>
                <input value={autofillData.district} disabled />
              </IconField>
            </div>

            <div className="loan-field loan-field--full">
              <label>Complete Address</label>
              <IconField icon={FiMapPin} className="loan-icon-field--textarea">
                <textarea rows="2" value={autofillData.address} disabled />
              </IconField>
            </div>
          </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title="3. Upload Documents"
              description=""
              badge=""
            />

          <div className="loan-form-grid loan-form-grid--3">

<div className={`loan-field loan-field--upload apply-now1-doc-card ${aadhaarFrontError ? "has-error" : ""}`}>
  <label>Aadhaar Front <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.aadhaarFront} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.aadhaarFront.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.aadhaarFront,
        setAadhaarFront,
        setAadhaarFrontError
      )
    }
  />
  <SelectedFileText file={aadhaarFront} />
  {aadhaarFrontError && <p className="loan-field-error">{aadhaarFrontError}</p>}
</div>

<div className={`loan-field loan-field--upload apply-now1-doc-card ${aadhaarBackError ? "has-error" : ""}`}>
  <label>Aadhaar Back <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.aadhaarBack} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.aadhaarBack.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.aadhaarBack,
        setAadhaarBack,
        setAadhaarBackError
      )
    }
  />
  <SelectedFileText file={aadhaarBack} />
  {aadhaarBackError && <p className="loan-field-error">{aadhaarBackError}</p>}
</div>

<div className={`loan-field loan-field--upload apply-now1-doc-card ${panCardError ? "has-error" : ""}`}>
  <label>PAN Card <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.panCard} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.panCard.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.panCard,
        setPanCardFile,
        setPanCardError
      )
    }
  />
  <SelectedFileText file={panCardFile} />
  {panCardError && <p className="loan-field-error">{panCardError}</p>}
</div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${bankStatementError ? "has-error" : ""}`}>
              <label>Bank Statement <Req /></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.bankStatement} />
              <div className="loan-upload-stack">
                <input
                  type="file"
                  accept={DOCUMENT_FILE_RULES.bankStatement.accept}
                  onChange={(e) => {
                    handleDocumentChange(
                      e,
                      DOCUMENT_FILE_RULES.bankStatement,
                      setBankStatement,
                      setBankStatementError
                    );
                    if (!e.target.files?.[0]) setBankStatementPassword("");
                  }}
                />
                <SelectedFileText file={bankStatement} />
                <div className="loan-upload-password">
                  <span className="loan-upload-password__label">
                    PDF Password <span className="loan-upload-password__optional">(optional)</span>
                  </span>
                  <IconField icon={FiLock}>
                    <input
                      type="password"
                      className="loan-field-password"
                      placeholder="If PDF is protected"
                      value={bankStatementPassword}
                      onChange={(e) => setBankStatementPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </IconField>
                </div>
              </div>
              {bankStatementError && <p className="loan-field-error">{bankStatementError}</p>}
            </div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${salarySlipError ? "has-error" : ""}`}>
              <label>Salary Slip <Req /></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.salarySlip} />
              <div className="loan-upload-stack">
                <input
                  type="file"
                  accept={DOCUMENT_FILE_RULES.salarySlip.accept}
                  onChange={(e) => {
                    handleDocumentChange(
                      e,
                      DOCUMENT_FILE_RULES.salarySlip,
                      setSalarySlip,
                      setSalarySlipError
                    );
                    if (!e.target.files?.[0]) setSalarySlipPassword("");
                  }}
                />
                <SelectedFileText file={salarySlip} />
                <div className="loan-upload-password">
                  <span className="loan-upload-password__label">
                    PDF Password <span className="loan-upload-password__optional">(optional)</span>
                  </span>
                  <IconField icon={FiLock}>
                    <input
                      type="password"
                      className="loan-field-password"
                      placeholder="If PDF is protected"
                      value={salarySlipPassword}
                      onChange={(e) => setSalarySlipPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </IconField>
                </div>
              </div>
              {salarySlipError && <p className="loan-field-error">{salarySlipError}</p>}
            </div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${utilityBillError ? "has-error" : ""}`}>
              <label>Utility Bill <Req /></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.utilityBill} />
              <input
                type="file"
                accept={DOCUMENT_FILE_RULES.utilityBill.accept}
                onChange={(e) =>
                  handleDocumentChange(
                    e,
                    DOCUMENT_FILE_RULES.utilityBill,
                    setUtilityBill,
                    setUtilityBillError
                  )
                }
              />
              <SelectedFileText file={utilityBill} />
              {utilityBillError && <p className="loan-field-error">{utilityBillError}</p>}
            </div>
          </div>
          </div>

          <div className="apply-form-block apply-form-block--accent">
            <SectionHeader
              title={<>4. Selfie Verification <Req /></>}
              description=""
              badge=""
            />
            <div className={`loan-selfie-row ${selfieError ? "has-error" : ""}`}>
              <div className="apply-now1-selfie-copy">
                <strong>{selfie ? "Selfie captured successfully" : "Capture a clear front-facing selfie"}</strong>
                <p>Use good light and keep your face centered.</p>
                {selfieError && <p className="loan-field-error">{selfieError}</p>}
              </div>
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--primary"
                onClick={() => setShowCameraModal(true)}
              >
                {selfie ? "Retake Selfie" : "Capture Selfie"}
              </button>
              {selfie && (
                <img src={selfie} alt="selfie" className="loan-selfie-preview" />
              )}
            </div>
          </div>

          {loading && <div className="loader">Processing... Please wait...</div>}

          <div className="apply-flow-actions">
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--secondary"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="submit"
              className="apply-flow-btn apply-flow-btn--primary"
              disabled={
                submitting ||
                panVerifying ||
                panError !== "" ||
                incomeError !== "" ||
                loanError !== "" ||
                obligationError !== ""
              }
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>

        <SelfieCapture show={showCameraModal} handleClose={() => setShowCameraModal(false)} onCapture={handleCapture} />
      </div>
    </div>
  );
};

export default LoanForm1;
